export const pythonNotes = [
  {
    id: 3001,
    topic: "python",
    seriesKey: "python",
    title: "Python Basics",
    description:
      "An introduction to Python, its installation, writing a first program, fundamental data types, and basic I/O.",
    content: `
##  Introduction to Python
Python is a high-level, interpreted programming language known for its readability and versatility.
### Setup and Environment
1. **Installation:** Download the official installer from python.org.
2. **IDEs (Integrated Development Environments):** **VS Code** (lightweight and popular) and **PyCharm** (full-featured professional environment) are common choices.
3. **Interpreter:** Python code is executed line-by-line by the interpreter. You can run scripts or use the interactive shell.
4. **Versions:** **Python 3.x** is the current standard. Python 2.x is officially unsupported.
### Fundamentals
1. **Comments and Docstrings:**
    * Single-line comments start with \`#\`.
    * Docstrings use triple quotes (\`"""..."""\`) and are used for documenting modules, classes, or functions.
2. **Variables and Constants:** Variables do not require explicit type declaration. Python uses naming conventions (e.g., \`ALL_CAPS\` for constants).
    \`\`\`python
    # Variable assignment
    age = 30
    name = "Alice"
    PI = 3.14159 # Conventionally treated as a constant
    \`\`\`
### Data Types and I/O
| Type | Description | Example |
| :--- | :--- | :--- |
| \`int\` | Integers | \`10\` |
| \`float\` | Floating-point numbers | \`10.5\` |
| \`str\` | Text/Strings | \`"Hello"\` |
| \`bool\` | Boolean (True/False) | \`True\` |
| \`NoneType\` | Represents absence of a value | \`None\` |
1. **Type Conversion (Casting):** Use functions like \`int()\`, \`float()\`, and \`str()\` to convert between types.
2. **Basic I/O:**
    * \`print()\`: Displays output to the console.
    * \`input()\`: Reads input from the user (always returns a string).
    \`\`\`python
    user_input = input("Enter your name: ")
    print(f"Welcome, {user_input}!")
    \`\`\`
`,
    tags: [
      "Python",
      "Basics",
      "Setup",
      "Variables",
      "Data Types",
      "I/O",
      "print",
      "input",
    ],
    date: "2025-10-02",
  },
  {
    id: 3002,
    topic: "python",
    seriesKey: "python",
    title: "Operators and Expressions",
    description:
      "A complete guide to Python's operators, including arithmetic, logical, bitwise, and special membership/identity operators.",
    content: `
##  Operators & Expressions
Operators are constructs that can manipulate the value of operands.
### Arithmetic Operators
| Operator | Name | Example | Result |
| :--- | :--- | :--- | :--- |
| \`+\` | Addition | \`5 + 2\` | \`7\` |
| \`/\` | Division | \`10 / 3\` | \`3.33...\` |
| \`//\` | Floor Division | \`10 // 3\` | \`3\` |
| \`%\` | Modulus | \`10 % 3\` | \`1\` |
| \`**\` | Exponentiation | \`2 ** 3\` | \`8\` |
### Comparison and Logical Operators
1. **Comparison:** \`==\`, \`!=\`, \`<\`, \`>\`, \`<=\`, \`>=\`. Return boolean values.
2. **Logical:** **\`and\`**, **\`or\`**, **\`not\`**. Used to combine conditional statements.
### Special Operators
1. **Membership Operators:**
    * \`in\`: Returns \`True\` if a sequence contains the specified value.
    * \`not in\`: Returns \`True\` if a sequence does not contain the specified value.
2. **Identity Operators:**
    * \`is\`: Returns \`True\` if both variables point to the same object in memory.
    * \`is not\`: Returns \`True\` if both variables do not point to the same object.
3. **Bitwise Operators:** \`&\` (AND), \`|\` (OR), \`^\` (XOR), \`~\` (NOT), \`<<\` (Left Shift), \`>>\` (Right Shift).
### Precedence and Associativity
Python follows standard mathematical precedence rules (PEMDAS-like). Parentheses **\`()\`** can always be used to enforce evaluation order.
`,
    tags: [
      "Python",
      "Operators",
      "Arithmetic",
      "Logical",
      "Bitwise",
      "Membership",
      "Precedence",
    ],
    date: "2025-10-02",
  },
  {
    id: 3003,
    topic: "python",
    seriesKey: "python",
    title: "Python Control Flow",
    description:
      "Directing program execution using if/elif/else structures and controlling iteration using for and while loops.",
    content: `
##  Control Flow
Control flow structures manage the order in which statements are executed. Python relies on **indentation** for defining blocks of code.
### Conditional Statements
Used for decision making based on conditions.
1. **\`if\`**: Executes code if a condition is true.
2. **\`if-else\`**: Executes one block if true, and an alternate block if false.
3. **\`elif\`**: (Else If) Allows checking multiple conditions sequentially.
4. **Nested Conditionals:** Placing one \`if/else\` structure inside another.
\`\`\`python
score = 85
if score >= 90:
    grade = "A"
elif score >= 80:
    grade = "B"
else:
    grade = "C"
print(grade) # Output: B
\`\`\`
### Loops
Used for repetitive execution of a block of code.
1. **\`for\` loop:** Used for iterating over a sequence (list, tuple, dictionary, string, or range).
    \`\`\`python
    for i in range(3): # iterates 0, 1, 2
        print(i)
    \`\`\`
2. **\`while\` loop:** Executes repeatedly as long as a given condition is true.
### Loop Control Statements
1. **\`break\`**: Terminates the loop entirely.
2. **\`continue\`**: Skips the rest of the current iteration and moves to the next.
3. **\`pass\`**: A null statement; nothing happens when it executes. Used as a placeholder where a statement is syntactically required.
4. **\`else\` with loops:** The \`else\` block executes immediately after a loop finishes **naturally** (i.e., not terminated by \`break\`).
`,
    tags: [
      "Python",
      "Control Flow",
      "if-else",
      "for loop",
      "while loop",
      "break",
      "continue",
    ],
    date: "2025-10-02",
  },
  {
    id: 3004,
    topic: "python",
    seriesKey: "python",
    title: "Python Data Structures",
    description:
      "Detailed review of Python's built-in collection types, their properties (mutability), key methods, and comprehensions.",
    content: `
##  Data Structures
Python provides four main built-in collection types:
### Lists (Mutable, Ordered)
1. **Creation, Indexing, Slicing:** Access elements using \`[]\`. Lists are **mutable** (can be changed).
2. **Methods:** \`append()\` (add to end), \`insert()\` (add at index), \`pop()\` (remove from end/index), \`remove()\` (remove by value), \`sort()\`, \`reverse()\`.
### Tuples (Immutable, Ordered)
* Defined with parentheses \`()\`. Tuples are immutable (cannot be changed after creation). Used for data that should not be modified.
### Sets (Mutable, Unordered, Unique)
* Defined with curly braces \`{}\`. Sets store only unique elements.
* Operations: \`union()\`, \`intersection()\`, \`difference()\`.
### Dictionaries (Mutable, Key-Value Pairs)
* Defined with curly braces \`{}\` and store data in key: value format. Keys must be unique and immutable (e.g., strings, tuples).
* Methods: \`get(key)\` (safer lookup), \`keys()\`, \`values()\`, \`items()\`.
### Strings
1. **Indexing & Slicing:** Treated as immutable sequences of characters.
2. **Methods:** \`split()\`, \`join()\`, \`replace()\`, \`strip()\`, f-strings(formatted string literals).
    \`\`\`python
    name = "John"
    greeting = f"Hello, {name}!"
    \`\`\`
### Comprehensions
Provide a concise way to create lists, dictionaries, or sets from existing iterables.
\`\`\`python
# List comprehension example
squares = [x**2 for x in range(5)] # [0, 1, 4, 9, 16]
# Dictionary comprehension example
names = {k: len(k) for k in ['a', 'b', 'c']} # {'a': 1, 'b': 1, 'c': 1}
\`\`\`
### Iterators & Generators
1. **Iterators:** Objects that represent a stream of data; implement \`__iter__\` and \`__next__\` methods.
2. **Generators:** Functions that return an iterator using the **\`yield\`** keyword (covered in Advanced Topics).
`,
    tags: [
      "Python",
      "Data Structures",
      "Lists",
      "Tuples",
      "Sets",
      "Dictionaries",
      "Comprehensions",
      "Mutability",
    ],
    date: "2025-10-02",
  },
  {
    id: 3005,
    topic: "python",
    seriesKey: "python",
    title: "Python Functions and Scope",
    description:
      "Defining functions, handling arguments, using the return statement, and understanding variable visibility.",
    content: `
##  Functions
Functions are reusable blocks of code that perform a specific task, promoting modularity and code reuse.
### Defining Functions and Arguments
* Use the \`def\` keyword to define a function.
1. **Return Statements:** The \`return\` keyword is used to send a value back to the caller. If omitted, the function returns \`None\`.
2. **Argument Types:**
    * Positional: Arguments matched by their order.
    * Keyword: Arguments matched by name (e.g., \`func(c=3, a=1)\`).
    * Default: Arguments assigned a default value if not provided by the caller.
    * Variable-length (\`*args\` and \`**kwargs\`):
         \`*args\`: Collects extra positional arguments into a tuple.
        * \`**kwargs\`: Collects extra keyword arguments into a dictionary.
### Lambda Functions
1. **Anonymous functions** defined using the **\`lambda\`** keyword. They can only contain one expression.
    \`\`\`python
    # Lambda function to add two numbers
    add = lambda x, y: x + y
    print(add(5, 3)) # Output: 8
    \`\`\`
### Scope (LEGB Rule)
Scope defines the region where a variable can be accessed. Python follows the **LEGB** rule:
1.  **L**ocal (inside the current function)
2.  **E**nclosing (inside enclosing functions, used by \`nonlocal\`)
3.  **G**lobal (at the top level of a module, used by \`global\`)
4.  **B**uilt-in (predefined names like \`print\`, \`len\`)
### Higher-Order Functions
Functions that take other functions as arguments or return a function.
1. **\`map(func, iterable)\`**: Applies a function to all elements in an iterable.
2. **\`filter(func, iterable)\`**: Constructs an iterator from elements of an iterable for which a function returns true.
3. **\`reduce(func, iterable)\`**: (From \`functools\`) Applies a function cumulatively to the items of an iterable.
`,
    tags: [
      "Python",
      "Functions",
      "Arguments",
      "Lambda",
      "Scope",
      "Recursion",
      "map",
      "filter",
    ],
    date: "2025-10-02",
  },
  {
    id: 3006,
    topic: "python",
    seriesKey: "python",
    title: "Python Modules, Packages, and pip",
    description:
      "Organizing code into modules and packages, and using the Python Standard Library and external libraries via pip.",
    content: `
##  Modules & Packages
### Modules
A **module** is simply a Python file (\`.py\`) containing definitions and statements.
 **Importing:**
    * \`import module_name\`: Access members using \`module_name.member\`.
    * \`from module_name import member\`: Imports specific members directly.
### Standard Library Overview
Python's vast standard library includes modules for common tasks:
1. **\`math\`**: Mathematical functions (trig, power, etc.).
2. **\`random\`**: Generating pseudo-random numbers.
3. **\`datetime\`**: Working with dates and times.
4. **\`os\`**: Interacting with the operating system (paths, environment variables).
5. **\`sys\`**: System-specific parameters and functions.
6. **\`re\`**: Regular expressions.
7. **\`json\`**: Handling JSON data.
### Packages
A **package** is a directory hierarchy of modules. It must contain a file named **\`__init__.py\`** (though often optional in modern Python, it's good practice) to indicate it's a Python package.
### Installing Packages via pip
**pip** is the standard package manager for Python. It allows you to install, uninstall, and manage third-party libraries (e.g., NumPy, Pandas).
\`\`\`bash
# Install a package
pip install package-name

# List installed packages
pip list
\`\`\`
`,
    tags: [
      "Python",
      "Modules",
      "Packages",
      "pip",
      "Standard Library",
      "import",
    ],
    date: "2025-10-02",
  },
  {
    id: 3007,
    topic: "python",
    seriesKey: "python",
    title: "Python File Handling and I/O",
    description:
      "Reading from and writing to files, using different file modes, and working with common data formats like CSV and JSON.",
    content: `
##  File Handling
Files are essential for persistent storage.
### Opening and Closing Files
1. **\`open(filename, mode)\`**: Opens a file and returns a file object.
2. **File Modes:**
    * \`r\`: Read (default).
    * \`w\`: Write (overwrites existing file).
    * \`a\`: Append (adds to the end).
    * \`r+\`: Read and Write.
3. **The \`with\` Statement (Recommended):** Automatically ensures the file is closed, even if errors occur. This is a form of a Context Manager.
    \`\`\`python
    with open("data.txt", "w") as f:
        f.write("Hello File!")
    # File is automatically closed here
    \`\`\`
### Reading and Writing Methods
1. **Reading:**
    * \`read(size)\`: Reads at most \`size\` bytes (or the entire file).
    * \`readline()\`: Reads a single line.
    * \`readlines()\`: Reads all lines into a list.
2. **Writing:**
    * \`write(string)\`: Writes a string to the file.
    * \`writelines(list_of_strings)\`: Writes a list of strings to the file.
### Working with CSV and JSON
1. **CSV (Comma Separated Values):** Use the built-in **\`csv\`** module for reading/writing structured data.
2. **JSON (JavaScript Object Notation):** Use the built-in **\`json\`** module for serialization/deserialization.
    * \`json.dump()\` / \`json.load()\` (to/from a file).
    * \`json.dumps()\` / \`json.loads()\` (to/from a string).
`,
    tags: [
      "Python",
      "File Handling",
      "I/O",
      "with statement",
      "JSON",
      "CSV",
      "open",
      "read",
      "write",
    ],
    date: "2025-10-02",
  },
  {
    id: 3008,
    topic: "python",
    seriesKey: "python",
    title: "Python Exception Handling",
    description:
      "Strategies for gracefully handling runtime errors, including using try/except blocks and defining custom exceptions.",
    content: `
##  Exception Handling
Exceptions are events that interrupt the normal flow of a program. Handling them prevents the program from crashing.
### Errors vs. Exceptions
1. **Errors (Syntax Errors):** Program cannot run (e.g., misspelled keyword).
2. **Exceptions (Runtime Errors):** Program runs but encounters an issue (e.g., \`ZeroDivisionError\`, \`NameError\`).
### The \`try/except\` Block
This is the core structure for handling exceptions.
1. **\`try\`**: The code that might raise an exception.
2. **\`except\`**: The code that executes if a specific exception is raised in the \`try\` block.
3. **\`else\`**: Executes only if the \`try\` block completes without an exception.
*4. **\`finally\`**: Always executes, regardless of whether an exception occurred (often used for cleanup, like closing resources).
\`\`\`python
try:
    result = 10 / num
except ZeroDivisionError:
    print("Cannot divide by zero!")
except TypeError:
    print("Invalid data type for division.")
except Exception as e: # Catch all other exceptions
    print(f"An unexpected error occurred: {e}")
else:
    print("Division successful.")
finally:
    print("Execution finished.")
\`\`\`
### Raising Custom Exceptions
You can manually trigger an exception using the **\`raise\`** keyword. You can also define **custom exception classes** by inheriting from the base \`Exception\` class.
`,
    tags: [
      "Python",
      "Exceptions",
      "try-except",
      "raise",
      "ZeroDivisionError",
      "Custom Exceptions",
    ],
    date: "2025-10-02",
  },
  {
    id: 3009,
    topic: "python",
    seriesKey: "python",
    title: "Python Object-Oriented Programming (OOP)",
    description:
      "Core principles of OOP in Python: classes, inheritance, encapsulation, polymorphism, and magic methods.",
    content: `
##  Object-Oriented Programming (OOP)
OOP is a programming paradigm based on the concept of "objects," which can contain data (attributes) and code (methods).
### Classes & Objects
1. **Class:** A blueprint for creating objects (e.g., \`class Car:\`).
2. **Object:** An instance of a class (e.g., \`my_car = Car()\`).
3.**\`__init__\` Constructor:** A special method called when an object is created. It initializes the object's attributes.
4. **\`self\` Keyword:** Represents the instance of the class itself. It must be the first parameter of any instance method.
### Pillars of OOP
1.  **Encapsulation:** Bundling data (attributes) and methods that operate on the data into a single unit (the class). Python uses conventions for pseudo-private/protected access:
    * Public: Normal attributes.
    * Protected: Use a single leading underscore (e.g., \`_attribute\`).
    * Private: Use two leading underscores (e.g., \`__attribute\`).
2.  **Inheritance:** A mechanism where a new class (child/derived) inherits properties and methods from an existing class (parent/base).
    * Method Overriding: Redefining a method in the child class that is already defined in the parent class.
3.  **Polymorphism:** The ability of an object to take on many forms (e.g., a method having different behaviors in different classes).
### Advanced Class Features
1. **Class & Static Methods:**
    * \`@classmethod\`: Methods bound to the class, not the instance. They receive the class itself (\`cls\`) as the first argument.
    * \`@staticmethod\`: Methods not bound to the instance or the class. They don't receive \`self\` or \`cls\`.
2. **Magic Methods (Dunder Methods):** Special methods surrounded by double underscores (e.g., \`__str__\`, \`__repr__\`, \`__add__\`). They allow classes to integrate with Python's built-in behavior (e.g., \`__add__\` defines what happens when you use the \`+\` operator).
`,
    tags: [
      "Python",
      "OOP",
      "Classes",
      "Inheritance",
      "Polymorphism",
      "Encapsulation",
      "Magic Methods",
      "self",
    ],
    date: "2025-10-02",
  },
  {
    id: 3010,
    topic: "python",
    seriesKey: "python",
    title: "Python Advanced Concepts",
    description:
      "In-depth look at powerful Python features like generators, custom context managers, decorators, and concurrency models.",
    content: `
##  Advanced Topics
### Decorators
A **Decorator** is a function that takes another function as an argument, adds functionality, and returns a new function without modifying the original. They use the **\`@name\`** syntax.
\`\`\`python
def debug_decorator(func):
    def wrapper(*args, **kwargs):
        print(f"Calling {func.__name__}")
        return func(*args, **kwargs)
    return wrapper

@debug_decorator
def greet(name):
    return f"Hello, {name}"

greet("World") # Output: Calling greet / Hello, World
\`\`\`
### Generators and Iterators
1. **Generators:** Functions that use **\`yield\`** instead of \`return\`. They produce results one at a time, pausing execution and saving state until the next item is requested. This saves memory, especially for large sequences.
2. **Iterators:** Objects that allow you to traverse a container. \`iter()\` returns an iterator, and \`next()\` retrieves the next item.
### Context Managers
An object that defines the runtime context to be established when executing a \`with\` statement. It uses the magic methods \`__enter__\` and \`__exit__\` (as seen in file handling).
### Concurrency
1. **Multi-threading:** Allows multiple parts of a program to run concurrently. Due to the **GIL (Global Interpreter Lock)**, Python threads are best for **I/O-bound** tasks.
2. **Multi-processing:** Uses separate processes, each with its own interpreter and memory space, bypassing the GIL. Best for **CPU-bound** tasks.
3. **Asyncio:** A framework for writing **single-threaded concurrent** code using coroutines, ideal for high-performance I/O operations (like web requests).
### Pythonic Conventions
1. **PEP8:** The official style guide for Python code.
2. **EAFP vs LBYL:**
    * EAFP (Easier to Ask Forgiveness than Permission): Try code and handle errors (\`try/except\`). (More Pythonic)
    * LBYL (Look Before You Leap): Check conditions before performing an action (\`if exists: do_it\`).
`,
    tags: [
      "Python",
      "Advanced",
      "Decorators",
      "Generators",
      "Asyncio",
      "Concurrency",
      "GIL",
      "Context Manager",
    ],
    date: "2025-10-02",
  },
  {
    id: 3011,
    topic: "python",
    seriesKey: "python",
    title: "Data Science Fundamentals and Python Libraries",
    description:
      "A brief overview of the essential Python libraries used for data processing, analysis, visualization, and machine learning.",
    content: `
##  Data Science & Libraries
Python dominates the Data Science landscape due to its rich ecosystem of specialized, high-performance libraries.
### Core Libraries
1.  **NumPy (Numerical Python):** The foundational package for numerical computing.
    * Provides high-performance n-dimensional array objects (ndarrays).
    * Enables vectorization, which allows operations on entire arrays without explicit loops, leading to massive speed improvements.
2.  **Pandas:** The core library for data manipulation and analysis.
    * Introduces the DataFrame (a table-like 2D structure) and Series (a 1D labeled array).
    * Crucial for cleaning, transforming, and aggregating data.
3.  **Matplotlib & Seaborn:** Libraries for data visualization.
    * Matplotlib: The fundamental plotting library, offering fine-grained control over figures and plots.
    * Seaborn: Built on Matplotlib, providing a higher-level interface for drawing attractive and informative statistical graphics.
### Machine Learning
1. **Scikit-learn:** A library that provides simple and efficient tools for data mining and data analysis. It covers classification, regression, clustering, dimensionality reduction, and more.
2. **TensorFlow / PyTorch:** Deep learning frameworks used for building complex neural networks and AI models. TensorFlow is Google's primary tool, while PyTorch is favored in research.
`,
    tags: [
      "Python",
      "Data Science",
      "NumPy",
      "Pandas",
      "Matplotlib",
      "Scikit-learn",
      "TensorFlow",
      "Libraries",
    ],
    date: "2025-10-02",
  },
];
